import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  AlertCircle,
  Brain,
  CalendarDays,
  FileText,
  MessageSquare,
  TrendingUp,
  Users,
} from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { useAuth } from '@/features/auth'
import { useDocumentTitle } from '@/hooks/use-document-title'
import {
  useDashboardStats,
  StatsCard,
  RecentActivity,
  InsightsPanel,
  ActionCard,
} from '@/features/dashboard'

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
})

function DashboardPage() {
  useDocumentTitle('Dashboard')
  const navigate = useNavigate()
  const { userProfile, currentOrganization } = useAuth()
  const { data: stats, isLoading: statsLoading } = useDashboardStats()

  // Check if user has no organization
  if (!currentOrganization) {
    return (
      <MainLayout title="Dashboard" subtitle="Visão geral dos seus pacientes e atividades">
        <div className="card p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
            <AlertCircle className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Sem organização vinculada
          </h2>
          <p className="text-gray-600 mb-4">
            Você ainda não está vinculado a nenhuma organização.
            Entre em contato com o administrador para ser adicionado.
          </p>
          <button
            onClick={() => navigate({ to: '/settings' })}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 rounded-lg transition-all duration-200"
          >
            Ir para configurações
          </button>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout title="Dashboard" subtitle="Visão operacional da clínica">
      <div className="mb-5 rounded-lg border border-teal-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-medium text-teal-700">{currentOrganization.name}</p>
            <h2 className="mt-1 text-xl font-semibold text-gray-900">
              {userProfile?.full_name ? `Olá, ${userProfile.full_name}` : 'Painel da clínica'}
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Acompanhe relatos, alertas e pacientes que precisam de atenção antes dos próximos atendimentos.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate({ to: '/schedule' })}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              <CalendarDays className="h-4 w-4 text-gray-500" />
              Agenda
            </button>
            <button
              onClick={() => navigate({ to: '/insights' })}
              className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-700"
            >
              <AlertCircle className="h-4 w-4" />
              Revisar alertas
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Pacientes ativos"
          value={stats?.activePatients ?? 0}
          description="Pacientes sob seu cuidado"
          icon={Users}
          color="bg-blue-50 text-blue-600"
          isLoading={statsLoading}
          onClick={() => navigate({ to: '/patients' })}
        />

        <StatsCard
          title="Relatos recentes"
          value={stats?.reportsThisWeek ?? 0}
          description="Recebidos nos últimos 7 dias"
          icon={MessageSquare}
          color="bg-teal-50 text-teal-600"
          isLoading={statsLoading}
          onClick={() => navigate({ to: '/reports' })}
        />

        <StatsCard
          title="Análises geradas"
          value={stats?.totalInsights ?? 0}
          description="Insights disponíveis"
          icon={FileText}
          color="bg-purple-50 text-purple-600"
          isLoading={statsLoading}
          onClick={() => navigate({ to: '/reports' })}
        />

        <StatsCard
          title="Alertas pendentes"
          value={stats?.pendingInsights ?? 0}
          description="Requerem sua atenção"
          icon={TrendingUp}
          color="bg-amber-50 text-amber-600"
          highlight={stats?.pendingInsights ? stats.pendingInsights > 0 : false}
          isLoading={statsLoading}
          onClick={() => navigate({ to: '/insights' })}
        />
      </div>

      {/* Quick Actions */}
      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
        <ActionCard
          title="Pacientes"
          description="Abrir lista e prontuários"
          icon={Users}
          color="blue"
          onClick={() => navigate({ to: '/patients' })}
        />

        <ActionCard
          title="Relatos"
          description="Revisar mensagens das famílias"
          icon={FileText}
          color="teal"
          onClick={() => navigate({ to: '/reports' })}
        />

        <ActionCard
          title="Alertas clínicos"
          description="Priorizar sinais de risco"
          icon={Brain}
          color="purple"
          onClick={() => navigate({ to: '/insights' })}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <RecentActivity />
        <InsightsPanel />
      </div>
    </MainLayout>
  )
}
